import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tooltip,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { clientService } from '../services/clientService';
import { Client, ClientQueryParams } from '../types/clients';

const ClientsPage: React.FC = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState<Client | null>(null);

  const loadClients = async (params?: ClientQueryParams) => {
    setLoading(true);
    setError(null);
    try {
      const response = await clientService.listClients(params);
      setClients(response.clients);
    } catch (err) {
      setError('Failed to load clients. Please try again.');
      console.error('Error loading clients:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadClients();
  }, []);

  const handleSearch = () => {
    const params: ClientQueryParams = {};
    if (searchTerm) params.search = searchTerm;
    if (statusFilter) params.status = statusFilter;
    loadClients(params);
  };

  const handleViewDetails = (client: Client) => {
    setSelectedClient(client);
    setDetailsOpen(true);
  };

  const handleDeleteClick = (client: Client) => {
    setClientToDelete(client);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!clientToDelete) return;
    
    try {
      await clientService.deleteClient(clientToDelete.id);
      setClients(clients.filter(c => c.id !== clientToDelete.id));
      setDeleteConfirmOpen(false);
      setClientToDelete(null);
    } catch (err) {
      setError('Failed to delete client. Please try again.');
      console.error('Error deleting client:', err);
    }
  };

  const getStatusChipColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status) {
      case 'Active': return 'success';
      case 'Inactive': return 'default';
      case 'Suspended': return 'error';
      case 'Pending': return 'warning';
      default: return 'default';
    }
  };

  const getRiskRatingColor = (rating?: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (rating) {
      case 'Low': return 'success';
      case 'Medium': return 'warning';
      case 'High': return 'error';
      case 'Very High': return 'error';
      default: return 'default';
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          Client Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/clients/create')}
          aria-label="Create new client"
        >
          Add Client
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <Box sx={{ flex: 1, minWidth: '300px' }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search clients..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                aria-label="Search clients by business name, contact info, or EIN"
              />
            </Box>
            <Box sx={{ minWidth: '200px' }}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                  aria-label="Filter clients by status"
                >
                  <MenuItem value="">All Statuses</MenuItem>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                  <MenuItem value="Suspended">Suspended</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ minWidth: '120px' }}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleSearch}
                disabled={loading}
                aria-label="Apply search and filter criteria"
              >
                Search
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Business Name</TableCell>
                    <TableCell>EIN</TableCell>
                    <TableCell>Industry</TableCell>
                    <TableCell>Contact</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Risk Rating</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {clients.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          No clients found. Add your first client to get started.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    clients.map((client) => (
                      <TableRow key={client.id} hover>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {client.businessName}
                            </Typography>
                            {client.dbaName && (
                              <Typography variant="caption" color="text.secondary">
                                DBA: {client.dbaName}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>{client.ein}</TableCell>
                        <TableCell>{client.industry}</TableCell>
                        <TableCell>
                          <Box>
                            <Typography variant="body2">
                              {client.contactInfo.primaryContactName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {client.contactInfo.email}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={client.status}
                            color={getStatusChipColor(client.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {client.riskRating && (
                            <Chip
                              label={client.riskRating}
                              color={getRiskRatingColor(client.riskRating)}
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleViewDetails(client)}
                              aria-label={`View details for ${client.businessName}`}
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Client">
                            <IconButton
                              size="small"
                              onClick={() => {
                                // TODO: Navigate to edit client page
                                console.log('Edit client:', client.id);
                              }}
                              aria-label={`Edit ${client.businessName}`}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete Client">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(client)}
                              aria-label={`Delete ${client.businessName}`}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Client Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="md"
        fullWidth
        aria-labelledby="client-details-title"
      >
        <DialogTitle id="client-details-title">
          Client Details: {selectedClient?.businessName}
        </DialogTitle>
        <DialogContent>
          {selectedClient && (
            <Stack spacing={3} sx={{ mt: 1 }}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>Business Information</Typography>
                <Stack spacing={0.5}>
                  <Typography variant="body2">Business Name: {selectedClient.businessName}</Typography>
                  {selectedClient.dbaName && (
                    <Typography variant="body2">DBA Name: {selectedClient.dbaName}</Typography>
                  )}
                  <Typography variant="body2">EIN: {selectedClient.ein}</Typography>
                  <Typography variant="body2">Business Type: {selectedClient.businessType}</Typography>
                  <Typography variant="body2">Industry: {selectedClient.industry}</Typography>
                  <Typography variant="body2">Year Established: {selectedClient.yearEstablished}</Typography>
                  {selectedClient.annualRevenue && (
                    <Typography variant="body2">
                      Annual Revenue: ${selectedClient.annualRevenue.toLocaleString()}
                    </Typography>
                  )}
                  {selectedClient.numberOfEmployees && (
                    <Typography variant="body2">Employees: {selectedClient.numberOfEmployees}</Typography>
                  )}
                </Stack>
              </Box>
              
              <Box>
                <Typography variant="subtitle2" gutterBottom>Contact Information</Typography>
                <Stack spacing={0.5}>
                  <Typography variant="body2">Contact: {selectedClient.contactInfo.primaryContactName}</Typography>
                  {selectedClient.contactInfo.primaryContactTitle && (
                    <Typography variant="body2">Title: {selectedClient.contactInfo.primaryContactTitle}</Typography>
                  )}
                  <Typography variant="body2">Phone: {selectedClient.contactInfo.phone}</Typography>
                  <Typography variant="body2">Email: {selectedClient.contactInfo.email}</Typography>
                  {selectedClient.contactInfo.website && (
                    <Typography variant="body2">Website: {selectedClient.contactInfo.website}</Typography>
                  )}
                </Stack>
              </Box>
              
              <Stack direction="row" spacing={4}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>Address</Typography>
                  <Stack spacing={0.5}>
                    <Typography variant="body2">{selectedClient.address.street}</Typography>
                    <Typography variant="body2">
                      {selectedClient.address.city}, {selectedClient.address.state} {selectedClient.address.zipCode}
                    </Typography>
                    <Typography variant="body2">{selectedClient.address.country}</Typography>
                  </Stack>
                </Box>
                
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>Risk Assessment</Typography>
                  <Stack spacing={0.5}>
                    <Typography variant="body2">Status: {selectedClient.status}</Typography>
                    {selectedClient.creditScore && (
                      <Typography variant="body2">Credit Score: {selectedClient.creditScore}</Typography>
                    )}
                    {selectedClient.riskRating && (
                      <Typography variant="body2">Risk Rating: {selectedClient.riskRating}</Typography>
                    )}
                  </Stack>
                </Box>
              </Stack>
              
              {selectedClient.notes && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Notes</Typography>
                  <Typography variant="body2">{selectedClient.notes}</Typography>
                </Box>
              )}
              
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Created: {new Date(selectedClient.createdAt).toLocaleDateString()}
                  {' • '}
                  Updated: {new Date(selectedClient.updatedAt).toLocaleDateString()}
                </Typography>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        aria-labelledby="delete-confirm-title"
      >
        <DialogTitle id="delete-confirm-title">Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {clientToDelete?.businessName}? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientsPage;